import { useState, useCallback } from "react";
import * as DocumentPicker from "expo-document-picker";
import BillServices from "@/services/billServices";
import { UserBill } from "@/types/bill";
import { showError, showSuccess } from "@/utils/toast";

interface UseBillUploadReturn {
  isUploading: boolean;
  uploadProgress: number;
  uploadStatus: "idle" | "uploading" | "analyzing";
  selectAndUploadBill: () => Promise<void>;
  uploadBill: (_file: DocumentPicker.DocumentPickerAsset) => Promise<boolean>;
}

export const useBillUpload = (
  onSuccess?: (_response: UserBill) => void,
  onError?: (_error: string) => void
): UseBillUploadReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "analyzing"
  >("idle");

  const uploadBill = useCallback(
    async (file: DocumentPicker.DocumentPickerAsset): Promise<boolean> => {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus("uploading");

      try {
        const uploadedBill = await BillServices.uploadBill(
          {
            uri: file.uri,
            name: file.name,
            type: file.mimeType || "application/pdf",
          },
          (progress) => {
            // Map actual upload progress to 0-75%
            const mappedProgress = Math.min(75, Math.round(progress * 0.75));
            setUploadProgress(mappedProgress);

            // When upload completes, switch to analyzing status
            if (progress >= 100) {
              setUploadStatus("analyzing");
              // Set progress to 75% when starting analysis
              setUploadProgress(75);
            }
          }
        );

        if (uploadedBill) {
          // Set progress to 100% when analysis is complete
          setUploadProgress(100);
          showSuccess("Bill uploaded successfully!");

          // Call success callback
          onSuccess?.(uploadedBill);

          return true;
        } else {
          showError("Failed to upload bill");
          return false;
        }
      } catch (error: any) {
        console.error("Upload error:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to upload bill. Please try again.";
        showError(errorMessage);
        onError?.(errorMessage);
        return false;
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
        setUploadStatus("idle");
      }
    },
    [onSuccess, onError]
  );

  const selectAndUploadBill = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });

      console.log("🔍 DocumentPicker result:", result);

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const selectedFile = result.assets[0];
      console.log("🔍 Selected file details:", {
        name: selectedFile.name,
        size: selectedFile.size,
        mimeType: selectedFile.mimeType,
        uri: selectedFile.uri,
        uriType: selectedFile.uri?.startsWith("blob:")
          ? "blob"
          : selectedFile.uri?.startsWith("data:")
          ? "data"
          : "other",
      });

      // Check file size (10MB limit)
      if (selectedFile.size && selectedFile.size > 10 * 1024 * 1024) {
        showError("File size must be less than 10MB");
        return;
      }

      await uploadBill(selectedFile);
    } catch (error) {
      console.error("Error selecting document:", error);
      showError("Failed to select document");
    }
  }, [uploadBill]);

  return {
    isUploading,
    uploadProgress,
    uploadStatus,
    selectAndUploadBill,
    uploadBill,
  };
};
